import { toUrlQueryString } from '../../utils';
/*
 * READ THIS BEFORE MAKING CHANGES TO THIS FILE.
 *
 * All names used in here must match names used in Alert Service API spec.
 * See https://gitlab.juvare.com/alert/alert-back-end/-/blob/main/web-api/src/main/api/api.yaml?ref_type=heads
 *
 * If for whatever reason you need to deviate from the naming scheme used in Alert Service API spec, then each such
 * deviation MUST be called with a comment describe the reason for the deviation.
 *
 * This file must be used only on platform-app backend code.
 */
/**
 * POST /alert/api/v1/tokens
 */
export function createToken(context, request) {
    const response = context.httpClient.post(`${context.appConfig.platformAlertBaseUrl}/alert/api/v1/tokens`, Object.assign(Object.assign({}, request), { secret: context.appConfig.platformAlertSecret }));
    if (response.code === 200) {
        return response.body;
    }
    throw new Error(response.errorBody);
}
/**
 * POST /alert/api/v1/alerts/{alertId}/responder-searches
 */
export function searchAlertResponders(context, createTokenRequest, params, request) {
    const token = createToken(context, createTokenRequest);
    const queryParams = { page: params.page, size: params.size };
    const response = context.httpClient.post(`${context.appConfig.platformAlertBaseUrl}/alert/api/v1/alerts/${params.alertId}/responder-searches${toUrlQueryString(queryParams)}`, Object.assign(Object.assign({}, request), { secret: context.appConfig.platformAlertSecret }), {
        Authorization: 'Bearer ' + token.jwt,
    });
    if (response.code === 200) {
        return response.body;
    }
    throw new Error(response.errorBody);
}
/**
 * GET /alert/api/v1/alerts
 */
export function getAlerts(context, createTokenRequest, params) {
    const queryParams = Object.assign({}, params);
    // IMPORTANT!!! ALERT SERVICE EXPECTS THESE STATUSES ONLY IN UPPERCASE, BUT THE SPEC USES LOWERCASE, NEED TO FIX THIS
    if (params.statuses) {
        queryParams.statuses = [...params.statuses].map((status) => status.toUpperCase());
    }
    const token = createToken(context, createTokenRequest);
    const response = context.httpClient.get(`${context.appConfig.platformAlertBaseUrl}/alert/api/v1/alerts${toUrlQueryString(queryParams)}`, {
        Authorization: 'Bearer ' + token.jwt,
    });
    if (response.code === 200) {
        return response.body;
    }
    throw new Error(response.errorBody);
}
/**
 * GET /alert/api/v1/alerts/{alertId}
 */
export function getAlert(context, createTokenRequest, alertId, excludeFields) {
    const token = createToken(context, createTokenRequest);
    const response = context.httpClient.get(`${context.appConfig.platformAlertBaseUrl}/alert/api/v1/alerts/${alertId}${toUrlQueryString({ excludeFields })}`, {
        Authorization: 'Bearer ' + token.jwt,
    });
    if (response.code === 200) {
        return response.body;
    }
    throw new Error(response.errorBody);
}
/**
 * PUT /alert/api/v1/alerts/{alertId}/send
 */
export function sendAlert(context, createTokenRequest, alertId) {
    console.log('Sending alert=', alertId);
    const token = createToken(context, createTokenRequest);
    const response = context.httpClient.put(`${context.appConfig.platformAlertBaseUrl}/alert/api/v1/alerts/${alertId}/send`, undefined, {
        Authorization: 'Bearer ' + token.jwt,
    });
    if (response.code === 200 || response.code === 204) {
        return;
    }
    throw new Error(response.errorBody);
}
/**
 *  PUT /alert/api/v1/alerts/{alertId}/recipients
 */
export function addRecipients(context, createTokenRequest, alertId, recipients) {
    const token = createToken(context, createTokenRequest);
    const response = context.httpClient.put(`${context.appConfig.platformAlertBaseUrl}/alert/api/v1/alerts/${alertId}/recipients`, recipients, {
        Authorization: 'Bearer ' + token.jwt,
    });
    if (response.code === 200 || response.code === 204) {
        return;
    }
    throw new Error(response.errorBody);
}
/**
 * PUT /alert/api/v1/alerts/{alertId}/terminate
 */
export function terminateAlert(context, createTokenRequest, alertId) {
    const token = createToken(context, createTokenRequest);
    const response = context.httpClient.put(`${context.appConfig.platformAlertBaseUrl}/alert/api/v1/alerts/${alertId}/terminate`, undefined, {
        Authorization: 'Bearer ' + token.jwt,
    });
    if (response.code === 200 || response.code === 204) {
        return;
    }
    throw new Error(response.errorBody);
}
/**
 * PUT /alert/api/v1/alerts/{alertId}/archive
 */
export function archiveAlert(context, createTokenRequest, alertId) {
    const token = createToken(context, createTokenRequest);
    const response = context.httpClient.put(`${context.appConfig.platformAlertBaseUrl}/alert/api/v1/alerts/${alertId}/archive`, undefined, {
        Authorization: 'Bearer ' + token.jwt,
    });
    if (response.code === 200 || response.code === 204) {
        return;
    }
    throw new Error(response.errorBody);
}
/**
 * DELETE /alert/api/v1/alerts/{alertId}
 */
export function deleteAlert(context, createTokenRequest, alertId) {
    const token = createToken(context, createTokenRequest);
    const response = context.httpClient.delete(`${context.appConfig.platformAlertBaseUrl}/alert/api/v1/alerts/${alertId}`, {
        Authorization: 'Bearer ' + token.jwt,
    });
    if (response.code === 200 || response.code === 204) {
        return;
    }
    throw new Error(response.errorBody);
}
/**
 * PUT /alert/api/v1/alerts/{alertId}
 */
export function updateAlert(context, createTokenRequest, alertId, alert) {
    const token = createToken(context, createTokenRequest);
    const response = context.httpClient.put(`${context.appConfig.platformAlertBaseUrl}/alert/api/v1/alerts/${alertId}`, alert, {
        Authorization: 'Bearer ' + token.jwt,
    });
    if (response && (response.code === 200 || response.code === 204)) {
        return;
    }
    throw new Error(response.errorBody);
}
/**
 * PUT /alert/api/v1/alerts/{alertId}/additional-data
 */
export function updateAlertAdditionalData(context, createTokenRequest, alertId, additionalData) {
    const token = createToken(context, createTokenRequest);
    const response = context.httpClient.put(`${context.appConfig.platformAlertBaseUrl}/alert/api/v1/alerts/${alertId}/additional-data`, additionalData, {
        Authorization: 'Bearer ' + token.jwt,
    });
    if (response.code === 200 || response.code === 204) {
        return;
    }
    throw new Error(response.errorBody);
}
/**
 * GET /alert/api/v1/alerts/{alertId}/recipients/contacts
 */
export function getRecipientsContacts(context, createTokenRequest, alertId, params) {
    const token = createToken(context, createTokenRequest);
    const response = context.httpClient.get(`${context.appConfig.platformAlertBaseUrl}/alert/api/v1/alerts/${alertId}/recipients/contacts${toUrlQueryString(params)}`, {
        Authorization: 'Bearer ' + token.jwt,
    });
    if (response.code === 200) {
        return response.body;
    }
    throw new Error(response.errorBody);
}
/**
 * GET /alert/api/v1/alerts/{alertId}/recipients/groups
 */
export function getRecipientsGroups(context, createTokenRequest, alertId, params) {
    const token = createToken(context, createTokenRequest);
    const response = context.httpClient.get(`${context.appConfig.platformAlertBaseUrl}/alert/api/v1/alerts/${alertId}/recipients/groups${toUrlQueryString(params)}`, {
        Authorization: 'Bearer ' + token.jwt,
    });
    if (response.code === 200) {
        return response.body;
    }
    throw new Error(response.errorBody);
}
/**
 * PUT /alert/api/v1/alerts/{alertId}/cancel
 * */
export function cancelAlert(context, createTokenRequest, alertId, 
/**
 * Picking recipients pending will revert status to recipients pending and clear all recipients in the alert.
 * @default draft
 * */
status) {
    console.log('Cancelling alert=', alertId, ' with status=', status !== null && status !== void 0 ? status : 'draft');
    const token = createToken(context, createTokenRequest);
    const response = context.httpClient.put(`${context.appConfig.platformAlertBaseUrl}/alert/api/v1/alerts/${alertId}/cancel?status=${status !== null && status !== void 0 ? status : 'draft'}`, undefined, { Authorization: 'Bearer ' + token.jwt });
    if (response.code === 204) {
        return response.body;
    }
    throw new Error(response.errorBody);
}
